import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { authAxios } from '../../contexts/AuthContext';
import { Settings as SettingsIcon, Save, Plus, Trash2, Tag, Edit2, Check, X, Building2, Truck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyHsn  = { code: '', description: '', gst_rate: '', cess_rate: '0' };
const emptyBiz  = { gstin: '', business_name: '', business_address: '', business_phone: '', business_email: '' };

export default function AdminSettings() {
  const { language, token } = useAuth();
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [savingBiz,    setSavingBiz]    = useState(false);
  const [savingHsn,    setSavingHsn]    = useState(false);
  const [returnWindowDays,  setReturnWindowDays]  = useState(7);
  const [deliveryCharge,    setDeliveryCharge]    = useState(0);
  const [freeDeliveryAbove, setFreeDeliveryAbove] = useState(0);
  const [savingDelivery,    setSavingDelivery]    = useState(false);
  const [bizInfo,      setBizInfo]      = useState(emptyBiz);
  const [hsnCodes,     setHsnCodes]     = useState([]);
  const [newHsn,       setNewHsn]       = useState(emptyHsn);
  const [showAddHsn,   setShowAddHsn]   = useState(false);
  const [editingHsnId, setEditingHsnId] = useState(null);
  const [editHsnData,  setEditHsnData]  = useState(emptyHsn);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchSettings = useCallback(async () => {
    try {
      const { data: d } = await authAxios.get(`${API}/admin/settings`, authHeaders);
      setReturnWindowDays(d.return_window_days ?? 7);
      setDeliveryCharge(d.delivery_charge ?? 0);
      setFreeDeliveryAbove(d.free_delivery_above ?? 0);
      setHsnCodes(d.hsn_codes || []);
      setBizInfo({
        gstin:            d.gstin            || '',
        business_name:    d.business_name    || '',
        business_address: d.business_address || '',
        business_phone:   d.business_phone   || '',
        business_email:   d.business_email   || '',
      });
    } catch { toast.error('Failed to fetch settings'); }
    finally  { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  /* ── Return Window ── */
  const handleSave = async () => {
    if (returnWindowDays < 1 || returnWindowDays > 30) { toast.error('Return window must be 1–30 days'); return; }
    setSaving(true);
    try {
      await authAxios.put(`${API}/admin/settings`, { return_window_days: parseInt(returnWindowDays) }, authHeaders);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  /* ── Delivery Charges ── */
  const handleSaveDelivery = async () => {
    const charge = parseFloat(deliveryCharge);
    const freeAbove = parseFloat(freeDeliveryAbove);
    if (isNaN(charge) || charge < 0) { toast.error('Delivery charge must be 0 or more'); return; }
    if (isNaN(freeAbove) || freeAbove < 0) { toast.error('Free delivery threshold must be 0 or more'); return; }
    setSavingDelivery(true);
    try {
      await authAxios.put(`${API}/admin/settings`, {
        delivery_charge: charge,
        free_delivery_above: freeAbove,
      }, authHeaders);
      toast.success('Delivery settings saved');
    } catch { toast.error('Failed to save delivery settings'); }
    finally { setSavingDelivery(false); }
  };

  /* ── Business / GSTIN ── */
  const handleSaveBiz = async () => {
    if (bizInfo.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(bizInfo.gstin)) {
      toast.error('Invalid GSTIN format  e.g. 09ABCDE1234F1Z5'); return;
    }
    setSavingBiz(true);
    try {
      await authAxios.put(`${API}/admin/settings`, { ...bizInfo }, authHeaders);
      toast.success('Business info saved');
    } catch { toast.error('Failed to save business info'); }
    finally { setSavingBiz(false); }
  };

  /* ── HSN helpers ── */
  const validateHsn = (h) => {
    if (!h.code?.trim())        return 'HSN code is required';
    if (!h.description?.trim()) return 'Description is required';
    if (isNaN(parseFloat(h.gst_rate))  || parseFloat(h.gst_rate)  < 0 || parseFloat(h.gst_rate)  > 100) return 'GST rate must be 0–100';
    if (isNaN(parseFloat(h.cess_rate)) || parseFloat(h.cess_rate) < 0 || parseFloat(h.cess_rate) > 100) return 'Cess rate must be 0–100';
    return null;
  };

  const saveHsnList = async (list) => {
    await authAxios.put(`${API}/admin/settings`, { hsn_codes: list }, authHeaders);
    setHsnCodes(list);
  };

  const handleAddHsn = async () => {
    const err = validateHsn(newHsn); if (err) { toast.error(err); return; }
    if (hsnCodes.some(h => h.code.trim() === newHsn.code.trim())) { toast.error('Code already exists'); return; }
    setSavingHsn(true);
    try {
      await saveHsnList([...hsnCodes, {
        id: Date.now().toString(),
        code: newHsn.code.trim(), description: newHsn.description.trim(),
        gst_rate: parseFloat(newHsn.gst_rate), cess_rate: parseFloat(newHsn.cess_rate ?? 0),
      }]);
      setNewHsn(emptyHsn); setShowAddHsn(false);
      toast.success('HSN code added');
    } catch { toast.error('Failed to add HSN code'); }
    finally { setSavingHsn(false); }
  };

  const handleSaveEditHsn = async (id) => {
    const err = validateHsn(editHsnData); if (err) { toast.error(err); return; }
    if (hsnCodes.some(h => h.id !== id && h.code.trim() === editHsnData.code.trim())) { toast.error('Code already exists'); return; }
    setSavingHsn(true);
    try {
      await saveHsnList(hsnCodes.map(h => h.id !== id ? h : {
        ...h, code: editHsnData.code.trim(), description: editHsnData.description.trim(),
        gst_rate: parseFloat(editHsnData.gst_rate), cess_rate: parseFloat(editHsnData.cess_rate ?? 0),
      }));
      setEditingHsnId(null);
      toast.success('HSN code updated');
    } catch { toast.error('Failed to update HSN code'); }
    finally { setSavingHsn(false); }
  };

  const handleDeleteHsn = async (id) => {
    if (!window.confirm('Delete this HSN code? Products using it will lose tax info.')) return;
    try {
      await saveHsnList(hsnCodes.filter(h => h.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-bold text-slate-900">
        {language === 'hi' ? 'ऐप सेटिंग्स' : 'App Settings'}
      </h1>

      {/* ── Business & GST Info card ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <Building2 className="w-6 h-6 text-green-600" />
          <div>
            <h2 className="text-lg font-bold">Business &amp; GST Info</h2>
            <p className="text-sm text-slate-500">Appears on all printed tax invoices</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Business Name</Label>
              <Input className="mt-1" value={bizInfo.business_name}
                onChange={e => setBizInfo({ ...bizInfo, business_name: e.target.value })}
                placeholder="AV Mart Shamli" />
            </div>
            <div>
              <Label>GSTIN</Label>
              <Input className="mt-1 font-mono tracking-wider uppercase" value={bizInfo.gstin}
                onChange={e => setBizInfo({ ...bizInfo, gstin: e.target.value.toUpperCase() })}
                placeholder="09ABCDE1234F1Z5" maxLength={15} />
              <p className="text-xs text-slate-400 mt-1">15-character GST Identification Number</p>
            </div>
          </div>
          <div>
            <Label>Business Address</Label>
            <Input className="mt-1" value={bizInfo.business_address}
              onChange={e => setBizInfo({ ...bizInfo, business_address: e.target.value })}
              placeholder="Shop No., Street, Shamli, Uttar Pradesh - 247776" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Phone</Label>
              <Input className="mt-1" value={bizInfo.business_phone}
                onChange={e => setBizInfo({ ...bizInfo, business_phone: e.target.value })}
                placeholder="+91-XXXXXXXXXX" />
            </div>
            <div>
              <Label>Email</Label>
              <Input className="mt-1" type="email" value={bizInfo.business_email}
                onChange={e => setBizInfo({ ...bizInfo, business_email: e.target.value })}
                placeholder="billing@avmartshamli.com" />
            </div>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <Button onClick={handleSaveBiz} disabled={savingBiz} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />{savingBiz ? 'Saving...' : 'Save Business Info'}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Delivery Charges card ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <Truck className="w-6 h-6 text-green-600" />
          <div>
            <h2 className="text-lg font-bold">Delivery Charges</h2>
            <p className="text-sm text-slate-500">Applied automatically when an order is placed</p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="deliveryCharge">Flat Delivery Charge (₹)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">₹</span>
                <Input
                  id="deliveryCharge"
                  type="number" min="0" step="0.5"
                  value={deliveryCharge}
                  onChange={e => setDeliveryCharge(e.target.value)}
                  className="pl-7 max-w-xs"
                  placeholder="0"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Set to 0 for always-free delivery</p>
            </div>

            <div>
              <Label htmlFor="freeDeliveryAbove">Free Delivery Above (₹)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">₹</span>
                <Input
                  id="freeDeliveryAbove"
                  type="number" min="0" step="1"
                  value={freeDeliveryAbove}
                  onChange={e => setFreeDeliveryAbove(e.target.value)}
                  className="pl-7 max-w-xs"
                  placeholder="0"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Set to 0 to always charge delivery</p>
            </div>
          </div>

          {/* Live preview */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm space-y-1">
            <p className="font-semibold text-slate-700 mb-2">📦 Preview</p>
            {parseFloat(deliveryCharge) === 0 ? (
              <p className="text-green-700">✓ Delivery is always <strong>FREE</strong> for all orders</p>
            ) : parseFloat(freeDeliveryAbove) > 0 ? (
              <>
                <p className="text-slate-600">• Orders below <strong>₹{parseFloat(freeDeliveryAbove).toFixed(0)}</strong> → delivery charge: <strong>₹{parseFloat(deliveryCharge).toFixed(2)}</strong></p>
                <p className="text-green-700">• Orders <strong>₹{parseFloat(freeDeliveryAbove).toFixed(0)}</strong> or above → <strong>FREE delivery</strong></p>
              </>
            ) : (
              <p className="text-slate-600">• All orders → delivery charge: <strong>₹{parseFloat(deliveryCharge).toFixed(2)}</strong></p>
            )}
          </div>

          <div className="pt-2 border-t border-slate-100">
            <Button onClick={handleSaveDelivery} disabled={savingDelivery} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />
              {savingDelivery ? 'Saving...' : 'Save Delivery Settings'}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Return Policy card ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <SettingsIcon className="w-6 h-6 text-green-600" />
          <h2 className="text-lg font-bold">
            {language === 'hi' ? 'रिटर्न नीति' : 'Return Policy'}
          </h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label htmlFor="returnWindow">
              {language === 'hi' ? 'रिटर्न विंडो (दिन)' : 'Return Window (Days)'}
            </Label>
            <Input id="returnWindow" type="number" min="1" max="30"
              value={returnWindowDays} onChange={e => setReturnWindowDays(e.target.value)}
              className="mt-2 max-w-xs" />
            <p className="text-sm text-slate-500 mt-2">
              Number of days customers can request a return after delivery
            </p>
          </div>
          <div className="pt-4 border-t border-slate-200">
            <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
        <div className="mt-6 p-4 bg-blue-50 rounded-lg text-sm text-blue-800 space-y-1">
          <p className="font-semibold text-blue-900 mb-1">ℹ️ Note</p>
          <p>• This setting applies to all products globally</p>
          <p>• Individual products can be marked as "non-returnable"</p>
          <p>• Only delivered orders are eligible for returns</p>
        </div>
      </div>

      {/* ── HSN Codes card ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Tag className="w-6 h-6 text-green-600" />
            <h2 className="text-lg font-bold">HSN Code Management</h2>
          </div>
          <Button onClick={() => { setShowAddHsn(true); setNewHsn(emptyHsn); }} className="bg-green-600 hover:bg-green-700" size="sm">
            <Plus className="w-4 h-4 mr-1" />Add HSN
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          GST rate is split equally into <strong>CGST</strong> and <strong>SGST</strong>. <strong>Cess</strong> is additional. All product prices are treated as <strong>GST-inclusive</strong> for bill calculations.
        </p>

        {/* Add form */}
        {showAddHsn && (
          <div className="mb-5 p-4 border border-green-200 bg-green-50 rounded-lg">
            <p className="text-sm font-semibold text-green-800 mb-3">Add New HSN Code</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">HSN Code *</Label>
                <Input value={newHsn.code} onChange={e => setNewHsn({ ...newHsn, code: e.target.value })}
                  placeholder="e.g. 1006" className="mt-1 h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Description *</Label>
                <Input value={newHsn.description} onChange={e => setNewHsn({ ...newHsn, description: e.target.value })}
                  placeholder="e.g. Rice" className="mt-1 h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">GST Rate % *</Label>
                <Input type="number" min="0" max="100" step="0.5" value={newHsn.gst_rate}
                  onChange={e => setNewHsn({ ...newHsn, gst_rate: e.target.value })}
                  placeholder="5" className="mt-1 h-8 text-sm" />
                <p className="text-xs text-slate-400 mt-0.5">CGST &amp; SGST each = half</p>
              </div>
              <div>
                <Label className="text-xs">Cess % (if any)</Label>
                <Input type="number" min="0" max="100" step="0.25" value={newHsn.cess_rate}
                  onChange={e => setNewHsn({ ...newHsn, cess_rate: e.target.value })}
                  placeholder="0" className="mt-1 h-8 text-sm" />
                <p className="text-xs text-slate-400 mt-0.5">0 if not applicable</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={handleAddHsn} disabled={savingHsn} className="bg-green-600 hover:bg-green-700">
                <Check className="w-3 h-3 mr-1" />{savingHsn ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowAddHsn(false)}>
                <X className="w-3 h-3 mr-1" />Cancel
              </Button>
            </div>
          </div>
        )}

        {hsnCodes.length === 0 ? (
          <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg">
            <Tag className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No HSN codes added yet</p>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">HSN Code</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Description</th>
                  <th className="text-center px-3 py-2 font-medium text-slate-600">GST %</th>
                  <th className="text-center px-3 py-2 font-medium text-slate-600">CGST %</th>
                  <th className="text-center px-3 py-2 font-medium text-slate-600">SGST %</th>
                  <th className="text-center px-3 py-2 font-medium text-slate-600">Cess %</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {hsnCodes.map((hsn) => (
                  <tr key={hsn.id} className="hover:bg-slate-50">
                    {editingHsnId === hsn.id ? (
                      <>
                        <td className="px-3 py-2"><Input value={editHsnData.code} onChange={e => setEditHsnData({ ...editHsnData, code: e.target.value })} className="h-7 text-xs w-20" /></td>
                        <td className="px-3 py-2"><Input value={editHsnData.description} onChange={e => setEditHsnData({ ...editHsnData, description: e.target.value })} className="h-7 text-xs" /></td>
                        <td className="px-3 py-2" colSpan={3}>
                          <div className="flex gap-2 items-center justify-center">
                            <Input type="number" min="0" max="100" step="0.5" value={editHsnData.gst_rate}
                              onChange={e => setEditHsnData({ ...editHsnData, gst_rate: e.target.value })}
                              className="h-7 text-xs w-16" placeholder="GST%" />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Input type="number" min="0" max="100" step="0.25" value={editHsnData.cess_rate}
                            onChange={e => setEditHsnData({ ...editHsnData, cess_rate: e.target.value })}
                            className="h-7 text-xs w-16" placeholder="Cess%" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" className="h-7 px-2 bg-green-600 hover:bg-green-700" onClick={() => handleSaveEditHsn(hsn.id)} disabled={savingHsn}>
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setEditingHsnId(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-mono font-bold text-slate-800">{hsn.code}</td>
                        <td className="px-4 py-3 text-slate-600">{hsn.description}</td>
                        <td className="px-3 py-3 text-center"><span className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-0.5 rounded-full">{hsn.gst_rate}%</span></td>
                        <td className="px-3 py-3 text-center"><span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">{(hsn.gst_rate / 2).toFixed(2)}%</span></td>
                        <td className="px-3 py-3 text-center"><span className="inline-block bg-indigo-100 text-indigo-800 text-xs font-semibold px-2 py-0.5 rounded-full">{(hsn.gst_rate / 2).toFixed(2)}%</span></td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${(hsn.cess_rate ?? 0) > 0 ? 'bg-orange-100 text-orange-800' : 'bg-slate-100 text-slate-400'}`}>
                            {(hsn.cess_rate ?? 0).toFixed(2)}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600 hover:text-slate-900" onClick={() => handleEditHsn(hsn)}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteHsn(hsn.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
